import { Stack } from "@mui/material";
import React from "react";
import { useTenantData } from "contexts/tenant";
import { ChildMixUpdate, MaterialRead } from "src/store/api/generatedApi";
import { stateToChildMixUpdates } from "./conversions";
import { LayerEditorSize } from "./LayerBar";
import { MixLayerPanel } from "./MixLayerPanel";
import { SplitLayerDialog } from "./SplitLayerDialog";
import { LayerItem, MixLayerState } from "./types";
import { useLayerEditorController } from "./useLayerEditorController";

interface Props {
  initialState: MixLayerState[];
  resetVersion?: number;
  materials: MaterialRead[];
  onStateChange: (
    updates: ChildMixUpdate[],
    editedMixIds: ReadonlySet<number>
  ) => void;
  weightsEditable: boolean;
  massScaleFactor: number;
  size?: LayerEditorSize;
  manuallyLayeredMixIds?: ReadonlySet<number>;
  onResetMixLayers?: (mixId: number) => void;
  collapsedMixIds?: ReadonlySet<number>;
  onToggleCollapse?: (mixId: number) => void;
  renderMixSummary?: (mixId: number) => React.ReactNode;
}

export const LayerEditor = ({
  initialState,
  resetVersion,
  materials,
  onStateChange,
  weightsEditable,
  massScaleFactor,
  size = "compact",
  manuallyLayeredMixIds,
  onResetMixLayers,
  collapsedMixIds,
  onToggleCollapse,
  renderMixSummary,
}: Props) => {
  const { basket_layer_display_order } = useTenantData();
  const controller = useLayerEditorController({ initialState, resetVersion });
  const { mixStates, editedMixIds, balancesByMix } = controller;
  const [splitTarget, setSplitTarget] = React.useState<{
    mixId: number;
    layer: LayerItem;
  } | null>(null);
  const onStateChangeRef = React.useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  React.useEffect(() => {
    onStateChangeRef.current(stateToChildMixUpdates(mixStates), editedMixIds);
  }, [mixStates, editedMixIds]);

  const hasMultipleMixes = mixStates.length > 1;

  return (
    <Stack sx={{ height: "100%", gap: 2 }}>
      <Stack sx={{ flex: 1, overflow: "auto", gap: 3 }}>
        {mixStates.map((mix) => (
          <MixLayerPanel
            key={mix.mixId}
            mix={mix}
            summary={renderMixSummary?.(mix.mixId)}
            materials={materials}
            onWeightChange={(layerId, weight) => {
              controller.updateLayer({
                mixId: mix.mixId,
                layerId,
                update: { weight },
              });
            }}
            onDelete={(layerId) => {
              controller.deleteLayer({ mixId: mix.mixId, layerId });
            }}
            onAddLayer={(basketNumber, materialName, weight) => {
              controller.addLayer({
                mixId: mix.mixId,
                basketNumber,
                materialName,
                weight,
              });
            }}
            onDragEnd={({ active, over }) => {
              if (over === null) return;
              controller.moveLayerByDrag({
                mixId: mix.mixId,
                activeId: String(active.id),
                overId: String(over.id),
                displayOrder: basket_layer_display_order,
                sameBasketDrop: "ignore",
              });
            }}
            onSplitRequest={(layerId) => {
              const layer = mix.baskets
                .flatMap((basket) => basket.layers)
                .find((candidate) => candidate.id === layerId);
              if (layer) setSplitTarget({ mixId: mix.mixId, layer });
            }}
            weightsEditable={weightsEditable}
            massScaleFactor={massScaleFactor}
            balances={balancesByMix.get(mix.mixId) ?? []}
            showSteelGrades={hasMultipleMixes}
            size={size}
            manuallyLayered={manuallyLayeredMixIds?.has(mix.mixId) ?? false}
            onResetMixLayers={
              onResetMixLayers
                ? () => {
                    onResetMixLayers(mix.mixId);
                  }
                : undefined
            }
            collapsed={
              hasMultipleMixes && (collapsedMixIds?.has(mix.mixId) ?? false)
            }
            onToggleCollapse={
              hasMultipleMixes && onToggleCollapse
                ? () => {
                    onToggleCollapse(mix.mixId);
                  }
                : undefined
            }
          />
        ))}
      </Stack>
      <SplitLayerDialog
        layer={splitTarget?.layer ?? null}
        onConfirm={(layerId, weight1, weight2) => {
          if (splitTarget === null) return;
          controller.splitLayer({
            mixId: splitTarget.mixId,
            layerId,
            weight1,
            weight2,
          });
        }}
        onClose={() => {
          setSplitTarget(null);
        }}
        massScaleFactor={massScaleFactor}
      />
    </Stack>
  );
};
